import { useParams } from "@tanstack/react-router";
import { Alert, Button, Card, Skeleton, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Breadcrumbs } from "@/shared/components/Breadcrumbs";
import { useEmployeeNames } from "@/shared/hooks/useEmployeeNames";
import { useRecentlyViewed } from "@/shared/hooks/useRecentlyViewed";
import { getErrorMessage } from "@/shared/utils/getErrorMessage";
import { AnalyticsSection } from "../components/detail/AnalyticsSection";
import { ExportSplitButton } from "../components/detail/ExportSplitButton";
import { ReportFilterBar } from "../components/detail/ReportFilterBar";
import { ReportTable } from "../components/ReportTable";
import { useReportFilters } from "../hooks/useReportFilters";
import {
	useDepartmentOptions,
	useReportAnalytics,
	useReportDescriptor,
	useReportRows,
	useReportSummary,
	useZoneOptions,
} from "../hooks/useReports";
import { reportDescription, reportTitle } from "../utils/reportI18n";
import "../styles/print.css";
import { ReportPrintView } from "./ReportPrintView";

const { Title, Text } = Typography;

const STANDART_LIMIT = 50;

/**
 * Bitta hisobot sahifasi: sarlavha (breadcrumbs, nom, tavsif, eksport,
 * chop etish) → filtr paneli → analitika bloki (kind layout'i) →
 * jadval. Davomat kind'larining layoutlari ham shu registrda
 * (`analyticsLayouts` ← `attendanceLayouts`).
 *
 * ⚠️ `useParams({ strict: false })` — marshrut obyekti
 * `routeTree.tsx` da (poydevor fayli) va uni import qilish blokdan
 * poydevorga qayta bog'lanish yaratardi (`LeadDetailPage` naqshi).
 *
 * ⚠️ Sahifalash holati (`page`/`limit`) SHU YERDA yashaydi, jadvalda
 * emas — filtr o'zgarganda uni birinchi sahifaga qaytarish bir joyda
 * hal bo'ladi (`DataTable` bilan bir xil asos).
 *
 * ⚠️⚠️ **Sahifa `lazyRouteComponent` GA O'TKAZILMAYDI.**
 * `styles/print.css` JSDoc'i o'lchangan faktni yozib qo'ygan: sahifa
 * `routeTree.tsx` da EAGER import qilingani uchun `print.css` asosiy
 * paketga tushadi va `@page landscape` butun ilovada ishlaydi.
 * Marshrutni lazy qilish `.skud-print-only` ni sahifa yuklanmaguncha
 * yo'q qiladi va chop etish JIMGINA buziladi. Qorovul —
 * `reports-bundle.test.ts`. Diagrammalar kutubxonasiz, sof `div`
 * bilan chizilgani uchun ajratish uchun sabab ham yo'q.
 */
export function ReportDetailPage() {
	const { t } = useTranslation("reports");
	const params = useParams({ strict: false }) as { kind?: string };
	const kind = params.kind ?? "";
	const { filters, setFilters } = useReportFilters();
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(STANDART_LIMIT);

	const { descriptor, isLoading: descriptorYuklanmoqda } = useReportDescriptor(kind);
	const { remember } = useRecentlyViewed();

	// ⚠️ Descriptor kelgandan KEYIN eslanadi — aks holda noma'lum kind
	// ham «Tez-tez ochilgan» ro'yxatiga tushardi (`LeadDetailPage`
	// naqshi). Yo'l `/reports/<kind>` — markaz (D1) shu prefiks bo'yicha
	// filtrlaydi; sanalar YOZILMAYDI (havola joriy oyni ochsin).
	useEffect(() => {
		if (descriptor) {
			remember({
				path: `/reports/${descriptor.kind}`,
				label: t("detail.recentlyViewed", { title: reportTitle(t, descriptor) }),
			});
		}
	}, [descriptor, remember, t]);
	const query = useReportRows(kind, filters, page, limit);

	// ⚠️ Xulosa so'rovi FAQAT chop etish uchun qoladi — `ReportPrintView`
	// raqamli xulosani `/summary` dan yozadi (reja F2: chop etish
	// o'zgarmaydi). Ekrandagi KPI va diagrammalar `/analytics`
	// konvertidan (`AnalyticsSection`). Darvoza — `descriptor.hasSummary`:
	// xulosasiz hisobotda so'rov yuborilsa 404 JIMGINA yiqilardi.
	const xulosaKerak = Boolean(descriptor?.hasSummary);
	const xulosa = useReportSummary(kind, filters, xulosaKerak);

	// ⚠️ Jadvaldagi «N kun» chizig'i uchun `matrix`. So'rov kaliti
	// `AnalyticsSection` dagi bilan AYNAN bir xil — TanStack Query uni
	// bitta so'rovga birlashtiradi. `matrix` faqat `attendance_monthly`
	// da to'la, boshqalarida `[]` — ustun `kind` literalisiz o'z-o'zidan
	// chizilmaydi.
	const analitika = useReportAnalytics(kind, filters, Boolean(descriptor));

	// ⚠️ Chop etish sarlavhasi UUID emas, YORLIQ ko'rsatadi — bo'lim va
	// hudud ro'yxatlari allaqachon keshda (filtr paneli o'sha so'rov
	// kalitlarini ishlatadi).
	//
	// ⚠️ **Xodim nomi ro'yxatdan OLINMAYDI.** `GET /employees` bitta
	// sahifa (`limit: 100`) qaytaradi va tanlangan xodim undan tashqarida
	// bo'lsa `find(...)` `undefined` berardi — «Xodim:» qatori chop
	// etilgan hujjatdan JIMGINA tushib qolardi, holbuki u audit dalili
	// (`ReportPrintView` JSDoc). `useEmployeeNames` kerakli ID ni
	// `GET /employees/{id}` orqali alohida oladi.
	const xodimNomlari = useEmployeeNames([filters.employeeId]);
	const { data: bolimlar = [] } = useDepartmentOptions();
	const { data: hududlar = [] } = useZoneOptions(Boolean(filters.zoneId));

	if (descriptorYuklanmoqda) {
		return <Skeleton active />;
	}

	if (!descriptor) {
		return <Alert type="error" showIcon message={t("detail.notFound")} />;
	}

	const ustunlar = query.data?.columns ?? descriptor.columns;
	// Spec §2.6: server matni o'zbekcha qoladi (XLSX), panel tarjima qiladi.
	const sarlavha = reportTitle(t, descriptor);
	const tavsif = reportDescription(t, descriptor);
	const jamiQator = query.data?.meta.total ?? 0;

	return (
		<Space orientation="vertical" size="middle" className="w-full">
			<div className="skud-no-print">
				{/*
				 * ⚠️ Uch bo'g'in, lekin BIRINCHISI navigatsiya guruhi va
				 * uning marshruti YO'Q. `Breadcrumbs` `to` siz bo'g'inni
				 * joriy sahifa deb qora va qalin chizadi, ya'ni birinchi
				 * bo'g'in ham qora chiqadi — kanvasdagi kulrang havoladan
				 * farqli. Bu ONGLI: aynan shu naqsh `EmployeeDetailPage` da
				 * ham ishlatiladi (`{ label: "Tashkilot" }`) va qabul
				 * qilingan; `Breadcrumbs.tsx` poydevor fayli, tahrirlanmaydi.
				 * `Breadcrumbs` o'zgargan kunda bu o'z-o'zidan tuzaladi.
				 */}
				<Breadcrumbs
					items={[
						{ label: t("detail.breadcrumbAnalytics") },
						{ label: t("list.title"), to: "/reports" },
						{ label: sarlavha },
					]}
				/>

				<div className="flex flex-wrap items-end gap-4 mt-2">
					<div>
						<Title level={3} className="!mb-1">
							{sarlavha}
						</Title>
						<Text type="secondary">
							{t("detail.subtitle", { description: tavsif, count: jamiQator })}
						</Text>
					</div>

					<Space wrap className="ml-auto">
						<ExportSplitButton kind={kind} filters={filters} />
						{/*
						 * ⚠️ Chop etish — BRAUZER ishi: server tomonida PDF
						 * generatsiyasi yo'q (spec R2). `print.css` chop etishda
						 * menyu, sarlavha va sahifalashni yashiradi.
						 */}
						<Button className="skud-no-print" onClick={() => window.print()}>
							{t("detail.printButton")}
						</Button>
					</Space>
				</div>
			</div>

			<ReportPrintView
				title={sarlavha}
				filters={filters}
				total={jamiQator}
				page={page}
				limit={limit}
				shownCount={query.data?.items.length ?? 0}
				summary={xulosa.data}
				employeeLabel={filters.employeeId ? xodimNomlari.get(filters.employeeId) : undefined}
				departmentLabel={bolimlar.find((bolim) => bolim.id === filters.departmentId)?.name}
				zoneLabel={hududlar.find((hudud) => hudud.id === filters.zoneId)?.name}
			/>

			<Card size="small" className="skud-no-print">
				<ReportFilterBar
					filters={filters}
					onChange={(next) => {
						// ⚠️ Filtr o'zgarganda sahifa BIRINCHIGA qaytadi — aks
						// holda 7-sahifada turgan foydalanuvchi tor filtrdan
						// keyin bo'sh jadval ko'rib, «ma'lumot yo'q» deb
						// xulosa qilardi.
						setPage(1);
						setFilters(next);
					}}
					columns={descriptor.columns}
				/>

				{/*
				 * ⚠️ «Hudud» va «Ish jadvali» chiplari `attendance_monthly`
				 * da CHIZILMAYDI: `baseWhere` `zoneId` ni umuman o'qimaydi
				 * va `ReportFilterBar` ni `zoneName` ustuni darvozalaydi (u
				 * darvoza TO'G'RI, tegilmaydi); `scheduleId` esa
				 * `ReportFilterState` tipida umuman yo'q — backend qarzi.
				 */}
				<Text type="secondary" className="mt-2 block text-xs">
					{t("detail.filterNote")}
				</Text>
			</Card>

			<AnalyticsSection kind={descriptor.kind} filters={filters} />

			{query.error ? (
				<Alert
					className="skud-no-print"
					type="error"
					showIcon
					message={getErrorMessage(query.error, t("detail.loadError"))}
				/>
			) : null}

			<Card
				size="small"
				title={t("detail.tableTitle")}
				extra={
					<Text type="secondary" className="text-xs">
						{t("detail.tableExtra")}
					</Text>
				}
				styles={{ body: { padding: 0 } }}
			>
				<ReportTable
					columns={ustunlar}
					rows={query.data?.items ?? []}
					loading={query.isLoading || query.isFetching}
					meta={query.data?.meta ?? { total: 0, page, limit, totalPages: 0 }}
					range={filters}
					showRate={xulosaKerak}
					kind={kind}
					matrix={analitika.data?.matrix}
					onPageChange={(keyingiSahifa, keyingiLimit) => {
						setPage(keyingiSahifa);

						if (keyingiLimit !== limit) {
							setLimit(keyingiLimit);
						}
					}}
				/>
			</Card>

			{/*
			 * ⚠️ **«Bildirishnoma keladi» va'dasi YOZILMAYDI.** Kanvas
			 * shunday va'da beradi, lekin `useReportExport` faqat polling
			 * qiladi va `message.info` ko'rsatadi — push bildirishnoma
			 * yo'q. Matn HAQIQATGA moslangan.
			 */}
			<Text type="secondary" className="text-xs skud-no-print">
				{t("detail.footerNote")}
			</Text>
		</Space>
	);
}
